'use client';

import { c as _c } from "react/compiler-runtime";
import { CheckIcon, Loader2Icon, XIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import type { InteractiveProposalActionController } from './interactive-proposal-state';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
type InteractiveProposalCardProps = {
  title: string;
  subtitle?: ReactNode;
  status?: ReactNode;
  children: ReactNode;
  action: InteractiveProposalActionController;
  warnings?: string[];
  applyLabel?: string;
  cancelLabel?: string;
  onApply: () => void | Promise<void>;
  onCancel: () => void;
  applyTestId?: string;
  cancelTestId?: string;
};
export type ProposalDiffRow = {
  label: string;
  before: string;
  after: string;
  multiline?: boolean;
};
export function ProposalStatusPill(t0) {
  const $ = _c(5);
  const {
    children,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "neutral" : t1;
  const t2 = variant === "success" ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "bg-muted text-muted-foreground";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("inline-flex h-5 items-center rounded-full px-2 font-medium text-[11px]", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children || $[3] !== t3) {
    t4 = <span className={t3}>{children}</span>;
    $[2] = children;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
export function ProposalDetailRow(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <div className="font-medium text-muted-foreground text-[11px] uppercase">{label}</div>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <div className="whitespace-pre-wrap break-words text-xs leading-relaxed">{value}</div>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="grid gap-1">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
export function ProposalDiff(t0) {
  const $ = _c(5);
  const {
    rows
  } = t0;
  if (rows.length === 0) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="text-muted-foreground text-xs">No field changes in this proposal.</div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== rows) {
    t1 = rows.map(_temp);
    $[1] = rows;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className="space-y-2">{t1}</div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function _temp(row) {
  return <div key={row.label} className="rounded-md border border-border/70 p-2"><div className="mb-2 font-medium text-muted-foreground text-[11px] uppercase">{row.label}</div><div className={cn("grid gap-2 text-xs", row.multiline ? "sm:grid-cols-1" : "sm:grid-cols-2")}><div className="min-w-0"><div className="mb-1 text-muted-foreground text-[11px]">Before</div><div className="whitespace-pre-wrap break-words rounded bg-muted/40 px-2 py-1.5">{row.before}</div></div><div className="min-w-0"><div className="mb-1 text-muted-foreground text-[11px]">After</div><div className="whitespace-pre-wrap break-words rounded bg-primary/5 px-2 py-1.5">{row.after}</div></div></div></div>;
}
export function InteractiveProposalCard(t0) {
  const $ = _c(48);
  const {
    title,
    subtitle,
    status,
    children,
    action,
    warnings: t1,
    applyLabel: t2,
    cancelLabel: t3,
    onApply,
    onCancel,
    applyTestId,
    cancelTestId
  } = t0;
  let t4;
  if ($[0] !== t1) {
    t4 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const warnings = t4;
  const applyLabel = t2 === undefined ? "Apply" : t2;
  const cancelLabel = t3 === undefined ? "Cancel" : t3;
  let t5;
  if ($[2] !== warnings) {
    t5 = warnings.join("\n");
    $[2] = warnings;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const warningText = t5;
  let t6;
  if ($[4] !== title) {
    t6 = <div className="font-medium text-foreground">{title}</div>;
    $[4] = title;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== subtitle) {
    t7 = subtitle ? <div className="mt-1 text-muted-foreground">{subtitle}</div> : null;
    $[6] = subtitle;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t6 || $[9] !== t7) {
    t8 = <div className="min-w-0">{t6}{t7}</div>;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== status || $[12] !== t8) {
    t9 = <div className="mb-3 flex items-start justify-between gap-3">{t8}{status}</div>;
    $[11] = status;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== children) {
    t10 = <div className="space-y-3">{children}</div>;
    $[14] = children;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t9) {
    t11 = <div className="rounded-md border border-border/70 bg-muted/20 p-3">{t9}{t10}</div>;
    $[16] = t10;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== warningText) {
    t12 = warningText ? <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-800 text-xs dark:text-amber-200">{warningText}</div> : null;
    $[19] = warningText;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== action.errorMessage) {
    t13 = action.errorMessage ? <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-destructive text-xs">{action.errorMessage}</div> : null;
    $[21] = action.errorMessage;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== action.statusMessage) {
    t14 = action.statusMessage ? <div className="text-muted-foreground text-xs">{action.statusMessage}</div> : null;
    $[23] = action.statusMessage;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const t15 = action.isApplying || action.isFinal;
  let t16;
  if ($[25] !== action.isApplying) {
    t16 = action.isApplying ? <Loader2Icon className="animate-spin" /> : <CheckIcon />;
    $[25] = action.isApplying;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== applyLabel || $[28] !== applyTestId || $[29] !== onApply || $[30] !== t15 || $[31] !== t16) {
    t17 = <Button type="button" size="sm" onClick={onApply} disabled={t15} data-testid={applyTestId}>{t16}{applyLabel}</Button>;
    $[27] = applyLabel;
    $[28] = applyTestId;
    $[29] = onApply;
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  const t18 = action.isApplying || action.isFinal;
  let t19;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <XIcon />;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== cancelLabel || $[35] !== cancelTestId || $[36] !== onCancel || $[37] !== t18) {
    t20 = <Button type="button" size="sm" variant="outline" onClick={onCancel} disabled={t18} data-testid={cancelTestId}>{t19}{cancelLabel}</Button>;
    $[34] = cancelLabel;
    $[35] = cancelTestId;
    $[36] = onCancel;
    $[37] = t18;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t17 || $[40] !== t20) {
    t21 = <div className="flex flex-wrap gap-2">{t17}{t20}</div>;
    $[39] = t17;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t11 || $[43] !== t12 || $[44] !== t13 || $[45] !== t14 || $[46] !== t21) {
    t22 = <div className="space-y-3 text-xs">{t11}{t12}{t13}{t14}{t21}</div>;
    $[42] = t11;
    $[43] = t12;
    $[44] = t13;
    $[45] = t14;
    $[46] = t21;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  return t22;
}